import { useEffect, useRef, useState } from "react";

interface CampaignOption {
    id: string;
    name: string;
}

interface CampaignPickerProps {
    campaigns: CampaignOption[];
    value: string | null;
    onChange: (id: string | null) => void;
    placeholder?: string;
}

export function CampaignPicker({ campaigns, value, onChange, placeholder = "Pick a campaign…" }: CampaignPickerProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const ref = useRef<HTMLDivElement>(null);

    const selected = campaigns.find((c) => c.id === value) ?? null;
    const filtered = query
        ? campaigns.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()))
        : campaigns;

    useEffect(() => {
        function onClickOutside(e: MouseEvent) {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
                setQuery("");
            }
        }
        if (open) {
            document.addEventListener("mousedown", onClickOutside);
            return () => document.removeEventListener("mousedown", onClickOutside);
        }
    }, [open]);

    return (
        <div ref={ref} className="relative inline-block w-full max-w-xs">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full text-left bg-okiru-bg border border-okiru-muted/30 rounded-md px-3 py-1.5 text-sm hover:border-okiru-accent transition flex items-center justify-between"
            >
                <span className={`truncate ${selected ? "" : "text-okiru-muted"}`}>
                    {selected ? selected.name : placeholder}
                </span>
                <span className="text-okiru-muted ml-2">▾</span>
            </button>
            {open && (
                <div className="absolute z-20 mt-1 w-72 bg-okiru-panel border border-okiru-muted/30 rounded-lg shadow-xl max-h-72 overflow-hidden flex flex-col">
                    <input
                        autoFocus
                        type="text"
                        placeholder="Search…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="bg-okiru-bg border-b border-okiru-muted/20 px-3 py-2 text-sm outline-none focus:border-okiru-accent"
                    />
                    <ul className="overflow-y-auto">
                        {value !== null && (
                            <li>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onChange(null);
                                        setOpen(false);
                                        setQuery("");
                                    }}
                                    className="w-full text-left px-3 py-2 text-xs text-okiru-muted italic hover:bg-okiru-bg"
                                >
                                    Clear selection
                                </button>
                            </li>
                        )}
                        {filtered.length === 0 ? (
                            <li className="px-3 py-2 text-xs text-okiru-muted">No campaigns match.</li>
                        ) : (
                            filtered.map((c) => (
                                <li key={c.id}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onChange(c.id);
                                            setOpen(false);
                                            setQuery("");
                                        }}
                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-okiru-bg truncate ${
                                            c.id === value ? "text-okiru-accent" : ""
                                        }`}
                                    >
                                        {c.name}
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
